import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomButton } from '@/shared/components/atoms';
import {
    Ticket as TicketIcon, Clock, User, Building,
    AlertCircle, Calendar, CheckCircle2, ChevronRight,
    Hash, X, MessageSquare, ShieldCheck, Home,
    History, LayoutDashboard, GraduationCap, ArrowRightCircle, RefreshCcw,
    GitBranch, Layers, UserCheck, Shuffle
} from 'lucide-react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import { ticketApi } from '../api/ticketApi';
import { departmentApi } from '@/features/school-admin/api/departmentApi';
import { workflowApi } from '@/features/school-admin/api/workflowApi';
import { Ticket, TicketStatus } from '../types';
import '../styles/ticketing.css';

const actionCommentSchema = z.object({
    comment: z.string().min(5, 'Please provide a reason (at least 5 characters)'),
});

const changeWorkflowSchema = z.object({
    department_id: z.string().min(1, 'Please select a department'),
    target_workflow_id: z.string().min(1, 'Please select a workflow'),
    comment: z.string().min(5, 'Please provide a reason (at least 5 characters)'),
});

type ChangeWorkflowFormData = z.infer<typeof changeWorkflowSchema>;

interface HistoryItem {
    id: string;
    action: string;
    from_step_id?: string | null;
    to_step_id?: string | null;
    actor_details: {
        name: string;
        email: string;
    };
    comment: string;
    created_at: string;
}

export const TicketDetailsPage: React.FC = () => {
    const { ticketId } = useParams<{ ticketId: string }>();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const schoolSlug = user?.school?.slug || '';

    const [ticket, setTicket] = useState<Ticket | null>(null);
    const [history, setHistory] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState<{ label: string; value: TicketStatus; color: string } | null>(null);

    // Change Workflow state
    const [isChangeWorkflowOpen, setIsChangeWorkflowOpen] = useState(false);
    const [isChangingWorkflow, setIsChangingWorkflow] = useState(false);
    const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
    const [workflowsForDept, setWorkflowsForDept] = useState<{ id: string; name: string }[]>([]);
    const [loadingWorkflows, setLoadingWorkflows] = useState(false);

    const {
        register: registerComment,
        handleSubmit: handleCommentSubmit,
        formState: { errors: commentErrors },
        reset: resetComment,
    } = useForm<{ comment: string }>({
        resolver: zodResolver(actionCommentSchema),
        defaultValues: { comment: '' }
    });

    const {
        register: registerChange,
        handleSubmit: handleChangeSubmit,
        formState: { errors: changeErrors },
        reset: resetChange,
        watch: watchChange,
        setValue: setChangeValue,
    } = useForm<ChangeWorkflowFormData>({
        resolver: zodResolver(changeWorkflowSchema),
        defaultValues: { department_id: '', target_workflow_id: '', comment: '' }
    });

    const selectedDeptId = watchChange('department_id');

    const fetchTicketDetails = useCallback(async () => {
        if (!schoolSlug || !ticketId) return;
        setLoading(true);
        setError(null);

        const response = await ticketApi.getTicketDetail(schoolSlug, ticketId, user?.user_id);

        if (response.success && response.data) {
            const ticketData = response.data.items ? response.data.items[0] : response.data;
            setTicket(ticketData);
            fetchHistory();
        } else {
            setError(response.error || 'Failed to load ticket details');
        }
        setLoading(false);
    }, [schoolSlug, ticketId, user?.user_id]);

    const fetchHistory = async () => {
        if (!schoolSlug || !ticketId) return;
        setLoadingHistory(true);
        const response = await ticketApi.getTicketHistory(schoolSlug, ticketId);
        if (response.success && response.data) {
            setHistory(response.data);
        }
        setLoadingHistory(false);
    };

    useEffect(() => {
        fetchTicketDetails();
    }, [fetchTicketDetails]);

    const handleUpdateStatus = async (data: { comment: string }) => {
        if (!schoolSlug || !ticketId || !ticket || !selectedAction) return;

        setIsUpdating(true);
        const response = await ticketApi.updateTicketStatus(schoolSlug, ticketId, {
            status: selectedAction.value,
            comment: data.comment
        });

        if (response.success) {
            showToast(`Ticket updated successfully`, 'success');
            setIsModalOpen(false);
            setSelectedAction(null);
            resetComment();
            fetchTicketDetails();
        } else {
            showToast(response.error || 'Failed to update ticket', 'error');
        }
        setIsUpdating(false);
    };

    const openActionModal = (action: { label: string; value: TicketStatus; color: string }) => {
        resetComment();
        setSelectedAction(action);
        setIsModalOpen(true);
    };

    const openChangeWorkflowModal = async () => {
        resetChange();
        setWorkflowsForDept([]);
        setIsChangeWorkflowOpen(true);
        // Load departments
        if (schoolSlug && departments.length === 0) {
            const res = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 1000 });
            if (res.success && res.data) {
                const data = res.data as any;
                const list = Array.isArray(data) ? data : (data.items || []);
                setDepartments(list.map((d: any) => ({ id: d.id, name: d.name })));
            }
        }
    };

    // Load workflows when department is selected
    useEffect(() => {
        const loadWorkflows = async () => {
            if (!selectedDeptId || !schoolSlug) {
                setWorkflowsForDept([]);
                return;
            }
            setLoadingWorkflows(true);
            setChangeValue('target_workflow_id', '');
            const res = await workflowApi.getWorkflowsByDepartment(schoolSlug, selectedDeptId);
            if (res.success && res.data) {
                setWorkflowsForDept((res.data || []).map((w: any) => ({ id: w.id, name: w.name })));
            } else {
                setWorkflowsForDept([]);
                showToast(res.error || 'Failed to load workflows', 'error');
            }
            setLoadingWorkflows(false);
        };
        loadWorkflows();
    }, [selectedDeptId, schoolSlug]);

    const handleChangeWorkflow = async (data: ChangeWorkflowFormData) => {
        if (!schoolSlug || !ticketId) return;
        setIsChangingWorkflow(true);
        const response = await ticketApi.changeTicketWorkflow(schoolSlug, ticketId, {
            target_workflow_id: data.target_workflow_id,
            comment: data.comment,
        });
        if (response.success) {
            showToast('Ticket reassigned successfully', 'success');
            setIsChangeWorkflowOpen(false);
            resetChange();
            fetchTicketDetails();
        } else {
            showToast(response.error || 'Failed to reassign ticket', 'error');
        }
        setIsChangingWorkflow(false);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'resolved': return '#10b981';
            case 'in_progress': return '#3b82f6';
            case 'open': return '#f59e0b';
            case 'rejected': return '#ef4444';
            case 'escalated': return '#f43f5e';
            case 'closed': return '#6b7280';
            default: return '#6366f1';
        }
    };

    const getAvailableActions = () => {
        if (!ticket) return [];
        const perms = ticket.user_permissions;
        const userRole = user?.role?.toUpperCase();
        const isAdmin = userRole === 'SCHOOL_ADMIN' || userRole === 'SUPER_ADMIN';

        const actions = [];

        // If ticket is resolved or closed, check for REOPEN permission
        if (ticket.status === 'resolved' || ticket.status === 'closed') {
            if (perms?.can_reopen || isAdmin) {
                actions.push({ label: 'Reopen Ticket', value: 'in_progress' as TicketStatus, color: '#f59e0b' });
            }
            return actions;
        }

        // Active ticket actions
        if (perms) {
            if (perms.can_resolve === true) {
                actions.push({ label: 'Resolve Ticket', value: 'resolved' as TicketStatus, color: '#10b981' });
            }
            if (perms.can_reply === true) {
                // Reply might just be adding a comment or setting to in_progress if it was open
                actions.push({ label: 'Send Reply', value: 'in_progress' as TicketStatus, color: '#06b6d4' });
            }
            if (perms.can_forward === true) {
                actions.push({ label: 'Forward Ticket', value: 'forwarded' as TicketStatus, color: '#8b5cf6' });
            }
        } else if (isAdmin) {
            actions.push({ label: 'Resolve Ticket', value: 'resolved' as TicketStatus, color: '#10b981' });
            actions.push({ label: 'Send Reply', value: 'in_progress' as TicketStatus, color: '#06b6d4' });
        }

        return actions;
    };

    const nextStatuses = getAvailableActions();

    const ShimmerBlock = ({ w, h, r = '12px', mb = '0' }: { w: string; h: string; r?: string; mb?: string }) => (
        <div style={{
            width: w, height: h, borderRadius: r, marginBottom: mb,
            background: 'linear-gradient(90deg, var(--tkt-surface2) 25%, var(--tkt-surface3) 50%, var(--tkt-surface2) 75%)',
            backgroundSize: '800px 100%',
            animation: 'tktShimmer 1.5s ease-in-out infinite',
            border: '1px solid var(--tkt-border)',
            opacity: 0.6
        }} />
    );

    const SkeletonLoader = () => (
        <div style={{ minHeight: '100vh', background: 'var(--tkt-bg)' }}>
            <style>{`
                @keyframes tktShimmer {
                    0% { background-position: -400px 0; }
                    100% { background-position: 400px 0; }
                }
            `}</style>

            <div className="tkt-hero-header" style={{ padding: '1.5rem 2rem 2.5rem', borderBottom: '1px solid var(--tkt-border)', background: 'rgba(255,255,255,0.02)' }}>
                <div className="tkt-page-wrapper">
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                        <ShimmerBlock w="60px" h="16px" r="4px" />
                        <ShimmerBlock w="80px" h="16px" r="4px" />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                            <ShimmerBlock w="120px" h="20px" mb="1rem" />
                            <ShimmerBlock w="300px" h="40px" mb="0.75rem" />
                        </div>
                        <ShimmerBlock w="120px" h="32px" r="100px" />
                    </div>
                </div>
            </div>

            <div className="tkt-page-wrapper tkt-main-grid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="tkt-overview-card">
                        <ShimmerBlock w="100%" h="120px" r="20px" />
                    </div>
                    <div style={{ background: 'var(--tkt-surface)', border: '1px solid var(--tkt-border)', borderRadius: '24px', padding: '2rem' }}>
                        <ShimmerBlock w="140px" h="24px" mb="1.5rem" />
                        <ShimmerBlock w="100%" h="150px" r="16px" />
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div style={{ background: 'var(--tkt-surface)', border: '1px solid var(--tkt-border)', borderRadius: '24px', padding: '1.5rem' }}>
                        <ShimmerBlock w="100%" h="200px" r="16px" />
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return <SkeletonLoader />;
    }

    if (error || !ticket) {
        return (
            <div style={{ height: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '1.5rem', padding: '2rem' }}>
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                    <AlertCircle size={40} />
                </div>
                <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Ticket Not Found</h2>
                    <p style={{ color: 'var(--tkt-text-muted)' }}>{error || "The ticket you're looking for doesn't exist or you don't have access."}</p>
                </div>
                <CustomButton onClick={() => navigate(`/${schoolSlug}/tickets`)} variant="outline">Back to Ticketing Portal</CustomButton>
            </div>
        );
    }

    return (
        <div className="tkt-root" style={{ paddingBottom: '5rem' }}>
            {/* Header / Breadcrumbs */}
            <div className="tkt-hero-header" style={{
                background: 'linear-gradient(180deg, rgba(99, 102, 241, 0.05) 0%, transparent 100%)',
                borderBottom: '1px solid var(--tkt-border)',
                padding: '1.25rem 2rem 1.5rem'
            }}>
                <div className="tkt-page-wrapper">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                        <CustomBreadcrumb
                            items={[
                                { label: 'Home', path: '/', icon: <Home size={14} /> },
                                { label: 'Ticketing Portal', path: `/${schoolSlug}/tickets`, icon: <TicketIcon size={14} /> },
                                { label: `Ticket #${ticket.id.slice(-6).toUpperCase()}`, icon: <Hash size={14} /> }
                            ]}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.25rem' }}>
                        <div style={{ flex: 1, minWidth: '300px' }}>
                            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                                <span className="tkt-detail-id-hero">#{ticket.id}</span>
                                {ticket.status === 'escalated' && (
                                    <span className="tkt-badge-pill escalated">
                                        <AlertCircle size={12} /> Escalated
                                    </span>
                                )}
                            </div> */}
                            <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 800, color: 'var(--tkt-text)', letterSpacing: '-0.02em', marginBottom: '0.75rem', lineHeight: 1.1 }}>
                                {ticket.subject || ticket.name}
                            </h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', color: 'var(--tkt-text-muted)', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                                {/* <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <User size={14} color="var(--tkt-accent)" />
                                    By <span style={{ color: 'var(--tkt-text)', fontWeight: 600 }}>{ticket.creator_details?.name || ticket.parents?.name || "Parent Account"}</span>
                                </span> */}
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Calendar size={14} /> {new Date(ticket.created_at).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Building size={14} /> {ticket.departments?.name || "General"}
                                </span>
                            </div>
                        </div>

                        {/* Action Dropdown — placed in header for quick access */}
                        {nextStatuses.length > 0 && (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <button
                                    onClick={openChangeWorkflowModal}
                                    style={{
                                        padding: '0.6rem 1.1rem', borderRadius: '10px',
                                        border: '1px solid var(--tkt-border)',
                                        background: 'var(--card-bg, #fff)',
                                        color: 'var(--text-primary)',
                                        fontSize: '0.85rem', fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--bg-secondary, rgba(0,0,0,0.03))'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = 'var(--card-bg, #fff)'; }}
                                    title="Reassign this ticket to the correct department"
                                >
                                    <Shuffle size={14} />
                                    Reassign Ticket
                                </button>
                                <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setIsActionsOpen(!isActionsOpen)}
                                    style={{
                                        padding: '0.6rem 1.25rem', borderRadius: '10px',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, var(--accent-primary, #6366f1), #4338ca)',
                                        color: '#ffffff',
                                        fontSize: '0.85rem', fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.35)';
                                    }}
                                >
                                    <ShieldCheck size={15} />
                                    Update Ticket
                                    <ChevronRight size={14} style={{ transform: isActionsOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                </button>

                                {isActionsOpen && (
                                    <>
                                        {/* Backdrop to close dropdown */}
                                        <div
                                            style={{ position: 'fixed', inset: 0, zIndex: 99 }}
                                            onClick={() => setIsActionsOpen(false)}
                                        />
                                        <div style={{
                                            position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                                            background: 'var(--card-bg, #fff)', border: '1px solid var(--tkt-border)',
                                            borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                                            minWidth: '220px', zIndex: 100, overflow: 'hidden',
                                            animation: 'tktSlideUp 0.2s ease'
                                        }}>
                                            {nextStatuses.map((status, idx) => (
                                                <button
                                                    key={status.value}
                                                    onClick={() => {
                                                        setIsActionsOpen(false);
                                                        openActionModal(status);
                                                    }}
                                                    disabled={isUpdating}
                                                    style={{
                                                        width: '100%', padding: '0.8rem 1.1rem',
                                                        border: 'none',
                                                        borderBottom: idx < nextStatuses.length - 1 ? '1px solid var(--tkt-border, rgba(0,0,0,0.06))' : 'none',
                                                        background: 'transparent',
                                                        color: status.color, fontSize: '0.85rem', fontWeight: 650,
                                                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                                                        display: 'flex', alignItems: 'center', gap: '0.65rem',
                                                        textAlign: 'left',
                                                        transition: 'background 0.15s ease',
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.background = `${status.color}10`}
                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: `${status.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <ArrowRightCircle size={14} color={status.color} />
                                                    </div>
                                                    {status.label}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="tkt-page-wrapper" style={{ marginTop: '1.25rem' }}>
                <div className="tkt-detail-grid">
                    {/* LEFT COLUMN: PRIMARY CONTENT */}
                    <div style={{ display: 'flex', flexDirection: 'column', }}>

                        {/* Stakeholders Card */}
                        <div className="tkt-detail-section" style={{ padding: '1rem' }}>
                            <div className="tkt-detail-section-title" style={{ fontSize: '0.65rem', marginBottom: '1rem' }}>
                                <User size={14} /> Submitted by
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
                                <div style={{ minWidth: '200px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '10px', background: '#06b6d4',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                                        boxShadow: '0 4px 10px rgba(6, 182, 212, 0.2)'
                                    }}>
                                        <User size={20} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{ticket.creator_details?.name || ticket.parents?.name || "Parent User"}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--tkt-cyan)' }}>{ticket.creator_details?.email || ticket.parents?.email || ticket.parents?.phone || "Contact not provided"}</div>
                                    </div>
                                </div>
                                {ticket.students && (
                                    <div style={{
                                        flex: 1, minWidth: '250px',
                                        background: 'transparent', padding: '0', borderRadius: '0',
                                        border: 'none', display: 'flex', alignItems: 'center', gap: '0.75rem'
                                    }}>
                                        <div style={{
                                            width: '40px', height: '40px', borderRadius: '10px', background: 'var(--tkt-accent)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                                            boxShadow: '0 4px 10px rgba(99, 102, 241, 0.2)'
                                        }}>
                                            <GraduationCap size={20} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '1rem', fontWeight: 700 }}>{ticket.students.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--tkt-text-muted)' }}>Class: <span style={{ color: 'var(--tkt-accent)', fontWeight: 600 }}>{ticket.students.class || ticket.students.class_name || "N/A"}</span></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Narrative / Description */}
                        <div className="tkt-detail-section">
                            <div className="tkt-detail-section-title">
                                <MessageSquare size={16} /> Issue Details
                            </div>
                            <div style={{
                                background: 'var(--tkt-surface2)', padding: '1.25rem', borderRadius: '12px',
                                border: '1px solid var(--tkt-border)', lineHeight: '1.6', color: 'var(--tkt-text)',
                                fontSize: '0.95rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                            }}>
                                {ticket.description || "No narrative provided."}
                            </div>
                        </div>

                        {/* Activity Timeline */}
                        <div className="tkt-detail-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <div className="tkt-detail-section-title" style={{ marginBottom: 0 }}>
                                    <History size={16} /> Activity History
                                </div>
                                {loadingHistory && <div className="tkt-loader-sm" />}
                            </div>

                            <div style={{ position: 'relative', paddingLeft: '1.5rem', borderLeft: '2px solid var(--tkt-border)', marginLeft: '0.75rem', marginTop: '1rem' }}>
                                {history.length > 0 ? (
                                    history.map((item, idx) => {
                                        const isLast = idx === history.length - 1;

                                        let ActionIcon = AlertCircle;
                                        let iconColor = 'var(--tkt-text-muted)';

                                        const action = item.action.toLowerCase();
                                        if (action.includes('resolved') || action.includes('resolve')) { ActionIcon = CheckCircle2; iconColor = 'var(--tkt-green)'; }
                                        else if (action.includes('escalated') || action.includes('escalate')) { ActionIcon = AlertCircle; iconColor = 'var(--tkt-red)'; }
                                        else if (action.includes('forwarded') || action.includes('forward')) { ActionIcon = ArrowRightCircle; iconColor = 'var(--tkt-accent)'; }
                                        else if (item.comment) { ActionIcon = MessageSquare; iconColor = 'var(--tkt-cyan)'; }
                                        else if (action.includes('status')) { ActionIcon = RefreshCcw; iconColor = 'var(--tkt-amber)'; }

                                        return (
                                            <div key={item.id} style={{
                                                position: 'relative',
                                                marginBottom: isLast ? 0 : '1.5rem'
                                            }}>
                                                {/* Dot / Icon */}
                                                <div style={{
                                                    position: 'absolute', left: '-2.15rem', top: '0',
                                                    width: '24px', height: '24px', borderRadius: '50%',
                                                    background: 'var(--tkt-surface)', border: `2px solid ${iconColor}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    zIndex: 2, color: iconColor
                                                }}>
                                                    <ActionIcon size={12} />
                                                </div>

                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--tkt-text)', textTransform: 'capitalize' }}>
                                                        {item.action === 'formalize_chat' ? 'Initial Submission' : item.action.replace(/_/g, ' ')}
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--tkt-text-dim)', fontWeight: 500, marginTop: '0.15rem' }}>
                                                            by <span style={{ color: 'var(--tkt-accent)' }}>{item.actor_details?.name || "System"}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: '0.7rem', color: 'var(--tkt-text-dim)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                        {new Date(item.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </div>

                                                {item.comment && (
                                                    <div style={{
                                                        marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--tkt-text-muted)',
                                                        lineHeight: '1.4', padding: '0.6rem 0.8rem', background: 'var(--tkt-surface2)',
                                                        borderRadius: '8px', borderLeft: `3px solid ${iconColor}`
                                                    }}>
                                                        {item.comment}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ color: 'var(--tkt-text-dim)', fontSize: '0.85rem' }}>
                                        No activity recorded yet.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: SIDEBAR / ACTIONS / META */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.50rem' }}>

                        {/* 0. Current Status Card */}
                        <div className="tkt-detail-section" style={{
                            padding: '1.25rem',
                            background: `${getStatusColor(ticket.status)}08`,
                            border: `1px solid ${getStatusColor(ticket.status)}30`,
                            marginBottom: '0'
                        }}>
                            <div style={{ fontSize: '0.65rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <AlertCircle size={12} color={getStatusColor(ticket.status)} /> Ticket Status
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{
                                    width: '36px', height: '36px', borderRadius: '10px',
                                    background: `${getStatusColor(ticket.status)}20`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: getStatusColor(ticket.status)
                                }}>
                                    <CheckCircle2 size={20} />
                                </div>
                                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: getStatusColor(ticket.status), textTransform: 'capitalize' }}>
                                    {ticket.status.replace(/_/g, ' ')}
                                </div>
                            </div>
                        </div>

                               {/* 4. SLA / Compliance */}
                        <div className="tkt-detail-section" style={{ padding: '1.25rem', background: 'rgba(217, 119, 6, 0.03)', border: '1px solid rgba(217, 119, 6, 0.1)' }}>
                            <div className="tkt-detail-section-title" style={{ color: 'var(--tkt-amber)', fontSize: '0.65rem', marginBottom: '1rem' }}>
                                <Clock size={14} />Ticket DueDate
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(217, 119, 6, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tkt-amber)' }}>
                                        <Calendar size={18} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Ticket DueDate</div>
                                        <div style={{ fontWeight: 800, fontSize: '0.85rem' }}>
                                            {ticket.total_sla_deadline ? new Date(ticket.total_sla_deadline).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : "No Target Set"}
                                        </div>
                                    </div>
                                </div>
                                {ticket.workflow_steps?.sla_hours != null && (
                                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(6, 182, 212, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tkt-cyan, #06b6d4)' }}>
                                            <Clock size={18} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Active Step DueDate</div>
                                            <div style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--tkt-cyan, #06b6d4)' }}>
                                                {ticket.workflow_steps.sla_hours} {ticket.workflow_steps.sla_hours === 1 ? 'hour' : 'hours'}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>   
                                          {/* 3. System Context */}
                        <div className="tkt-detail-section" style={{ padding: '1.25rem' }}>
                            <div className="tkt-detail-section-title" style={{ fontSize: '0.65rem', marginBottom: '1rem' }}>
                                <ShieldCheck size={14} /> Ticket Information
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                                {/* Department */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                                            <Building size={13} />
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Department</div>
                                        <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{ticket.departments?.name || "General"}</div>
                                    </div>
                                </div>
                                {/* Connector line */}
                                <div style={{ width: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: '2px', height: '8px', background: 'linear-gradient(to bottom, #6366f1, #0ea5e9)' }} />
                                    <ChevronRight size={12} color="#0ea5e9" style={{ transform: 'rotate(90deg)' }} />
                                </div>

                                {/* Workflow */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                                            <GitBranch size={13} />
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Assigned Workflow</div>
                                        <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{ticket.workflows?.name || "Support Level"}</div>
                                    </div>
                                </div>
                                {/* Connector line */}
                                <div style={{ width: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: '2px', height: '8px', background: 'linear-gradient(to bottom, #0ea5e9, #10b981)' }} />
                                    <ChevronRight size={12} color="#10b981" style={{ transform: 'rotate(90deg)' }} />
                                </div>

                                {/* Current Step */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                                            <Layers size={13} />
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Current Step</div>
                                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--tkt-cyan)' }}>{ticket.workflow_steps?.name || "Initial Assessment"}</div>
                                    </div>
                                </div>

                                {/* Assigned To */}
                                {ticket.assigned_employees && ticket.assigned_employees.length > 0 && (
                                    <>
                                        {/* Connector line */}
                                        <div style={{ width: '28px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div style={{ width: '2px', height: '8px', background: 'linear-gradient(to bottom, #10b981, #f59e0b)' }} />
                                            <ChevronRight size={12} color="#f59e0b" style={{ transform: 'rotate(90deg)' }} />
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                                                    <UserCheck size={13} />
                                                </div>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '0.6rem', color: 'var(--tkt-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Assigned To</div>
                                                <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                                    {ticket.assigned_employees.map((emp: any) => emp.name).join(', ')}
                                                </div>
                                                <div style={{ fontSize: '0.65rem', color: 'var(--tkt-text-muted)' }}>
                                                    {ticket.assigned_employees.map((emp: any) => emp.email).join(', ')}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        {/* 1. Control Center - Moved to header */}




                    </div>
                </div>
            </div>

            {/* Transition Modal */}
            {isModalOpen && selectedAction && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 2000, padding: '1rem'
                }}>
                    <div style={{
                        background: 'var(--tkt-surface)', width: '100%', maxWidth: '500px', borderRadius: '24px',
                        border: '1px solid var(--tkt-border)', boxShadow: 'var(--tkt-shadow-lg)',
                        overflow: 'hidden', animation: 'tktSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                    }}>
                        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: `linear-gradient(180deg, ${selectedAction.color}10 0%, transparent 100%)` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${selectedAction.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: selectedAction.color }}>
                                    <ArrowRightCircle size={20} />
                                </div>
                                <h3 style={{ fontSize: '1.15rem', fontWeight: 800 }}>{selectedAction.label}</h3>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--tkt-text-muted)', cursor: 'pointer', padding: '0.5rem' }}><X size={20} /></button>
                        </div>
                        <div style={{ padding: '0 1.5rem 1.5rem' }}>
                            <form onSubmit={handleCommentSubmit(handleUpdateStatus)}>
                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--tkt-text-muted)' }}>
                                        Reason / Remarks <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <textarea
                                        {...registerComment('comment')}
                                        placeholder="Briefly describe the reason for this action..."
                                        style={{
                                            width: '100%', height: '120px', padding: '1rem', borderRadius: '12px',
                                            background: 'var(--tkt-surface2)',
                                            border: `1px solid ${commentErrors.comment ? '#ef4444' : 'var(--tkt-border)'}`,
                                            color: 'var(--tkt-text)', outline: 'none', fontSize: '0.9rem',
                                            transition: 'all 0.2s', resize: 'none'
                                        }}
                                        onFocus={(e) => { if (!commentErrors.comment) e.currentTarget.style.borderColor = selectedAction.color; }}
                                        onBlur={(e) => { if (!commentErrors.comment) e.currentTarget.style.borderColor = 'var(--tkt-border)'; }}
                                    />
                                    {commentErrors.comment && (
                                        <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.4rem', fontWeight: 500 }}>
                                            {commentErrors.comment.message}
                                        </p>
                                    )}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '0.85rem' }}>
                                    <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '0.75rem', borderRadius: '12px', background: 'transparent', border: '1px solid var(--tkt-border)', color: 'var(--tkt-text)', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                                    <button type="submit" disabled={isUpdating} style={{ padding: '0.75rem', borderRadius: '12px', background: selectedAction.color, border: 'none', color: 'white', fontWeight: 800, cursor: isUpdating ? 'not-allowed' : 'pointer', boxShadow: `0 4px 15px ${selectedAction.color}40`, opacity: isUpdating ? 0.7 : 1 }}>
                                        {isUpdating ? 'Processing...' : `Confirm ${selectedAction.label.split(' ')[0]}`}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Workflow Modal */}
            {isChangeWorkflowOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(12px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 2000, padding: '1rem'
                }}>
                    <div style={{
                        background: 'var(--tkt-surface)', width: '100%', maxWidth: '520px', borderRadius: '20px',
                        border: '1px solid var(--tkt-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                        overflow: 'hidden', animation: 'tktSlideUp 0.3s ease'
                    }}>
                        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(180deg, rgba(99,102,241,0.08), transparent)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
                                    <Shuffle size={18} />
                                </div>
                                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0 }}>Reassign Ticket</h3>
                            </div>
                            <button onClick={() => setIsChangeWorkflowOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--tkt-text-muted)', cursor: 'pointer', padding: '0.5rem' }}><X size={20} /></button>
                        </div>
                        <div style={{ padding: '0 1.5rem 1.5rem' }}>
                            <p style={{ fontSize: '0.8rem', color: 'var(--tkt-text-muted)', marginBottom: '1.25rem' }}>
                                Was this ticket sent to the wrong place? Reassign it to the correct department and workflow.
                            </p>
                            <form onSubmit={handleChangeSubmit(handleChangeWorkflow)}>
                                <div style={{ marginBottom: '1.1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--tkt-text-muted)' }}>
                                        Correct Department <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <select
                                        {...registerChange('department_id')}
                                        style={{
                                            width: '100%', padding: '0.7rem 0.9rem', borderRadius: '10px',
                                            background: 'var(--tkt-surface2)',
                                            border: `1px solid ${changeErrors.department_id ? '#ef4444' : 'var(--tkt-border)'}`,
                                            color: 'var(--tkt-text)', fontSize: '0.9rem', outline: 'none', cursor: 'pointer'
                                        }}
                                    >
                                        <option value="">Choose the correct department</option>
                                        {departments.map(d => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                    {changeErrors.department_id && (
                                        <p style={{ color: '#ef4444', fontSize: '0.72rem', marginTop: '0.3rem', fontWeight: 500 }}>{changeErrors.department_id.message}</p>
                                    )}
                                </div>

                                <div style={{ marginBottom: '1.1rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--tkt-text-muted)' }}>
                                        Workflow to Assign <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <select
                                        {...registerChange('target_workflow_id')}
                                        disabled={!selectedDeptId || loadingWorkflows}
                                        style={{
                                            width: '100%', padding: '0.7rem 0.9rem', borderRadius: '10px',
                                            background: 'var(--tkt-surface2)',
                                            border: `1px solid ${changeErrors.target_workflow_id ? '#ef4444' : 'var(--tkt-border)'}`,
                                            color: 'var(--tkt-text)', fontSize: '0.9rem', outline: 'none',
                                            cursor: (!selectedDeptId || loadingWorkflows) ? 'not-allowed' : 'pointer',
                                            opacity: (!selectedDeptId || loadingWorkflows) ? 0.6 : 1,
                                        }}
                                    >
                                        <option value="">
                                            {!selectedDeptId ? 'Pick a department first' : loadingWorkflows ? 'Loading workflows...' : workflowsForDept.length === 0 ? 'No workflows available for this department' : 'Choose a workflow'}
                                        </option>
                                        {workflowsForDept.map(w => (
                                            <option key={w.id} value={w.id}>{w.name}</option>
                                        ))}
                                    </select>
                                    {changeErrors.target_workflow_id && (
                                        <p style={{ color: '#ef4444', fontSize: '0.72rem', marginTop: '0.3rem', fontWeight: 500 }}>{changeErrors.target_workflow_id.message}</p>
                                    )}
                                </div>

                                <div style={{ marginBottom: '1.25rem' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem', color: 'var(--tkt-text-muted)' }}>
                                        Reason for Reassignment <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <textarea
                                        {...registerChange('comment')}
                                        placeholder="Briefly explain why this ticket needs to be reassigned..."
                                        style={{
                                            width: '100%', height: '100px', padding: '0.85rem', borderRadius: '10px',
                                            background: 'var(--tkt-surface2)',
                                            border: `1px solid ${changeErrors.comment ? '#ef4444' : 'var(--tkt-border)'}`,
                                            color: 'var(--tkt-text)', outline: 'none', fontSize: '0.9rem', resize: 'none'
                                        }}
                                    />
                                    {changeErrors.comment && (
                                        <p style={{ color: '#ef4444', fontSize: '0.72rem', marginTop: '0.3rem', fontWeight: 500 }}>{changeErrors.comment.message}</p>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '0.75rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => setIsChangeWorkflowOpen(false)}
                                        disabled={isChangingWorkflow}
                                        style={{ padding: '0.75rem', borderRadius: '12px', background: 'transparent', border: '1px solid var(--tkt-border)', color: 'var(--tkt-text)', fontWeight: 700, cursor: 'pointer' }}
                                    >Cancel</button>
                                    <button
                                        type="submit"
                                        disabled={isChangingWorkflow}
                                        style={{
                                            padding: '0.75rem', borderRadius: '12px',
                                            background: 'linear-gradient(135deg, var(--accent-primary), #4338ca)',
                                            border: 'none', color: 'white', fontWeight: 800,
                                            cursor: isChangingWorkflow ? 'not-allowed' : 'pointer',
                                            boxShadow: '0 4px 15px rgba(99,102,241,0.4)',
                                            opacity: isChangingWorkflow ? 0.7 : 1
                                        }}
                                    >
                                        {isChangingWorkflow ? 'Reassigning...' : 'Reassign Ticket'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
